import { useEffect, useRef, useState } from "react";
import { animate, useInView } from "framer-motion";
import { ShieldCheck } from "lucide-react";
import { useLang } from "@/i18n";
import { Eyebrow, Reveal } from "./Reveal";

const Counter = ({ prefix, value, suffix }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  const [display, setDisplay] = useState(0);

  useEffect(() => {
    if (!inView) return;
    const controls = animate(0, value, {
      duration: 1.9,
      ease: [0.16, 1, 0.3, 1],
      onUpdate: (v) => setDisplay(Math.round(v)),
    });
    return () => controls.stop();
  }, [inView, value]);

  return (
    <span ref={ref} className="font-display text-5xl font-bold tracking-tight text-[#0A1F18] sm:text-6xl">
      {prefix}
      {display}
      {suffix}
    </span>
  );
};

export const Stats = () => {
  const { t } = useLang();

  return (
    <section data-testid="stats-section" className="bg-[#FBF8F3] py-24 sm:py-32">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid gap-12 lg:grid-cols-[1fr_1.6fr] lg:gap-20">
          <Reveal>
            <Eyebrow>{t.stats.eyebrow}</Eyebrow>
            <h2 className="mt-6 font-display text-3xl font-semibold leading-[1.15] tracking-tight text-[#0A1F18] sm:text-4xl lg:text-5xl">
              {t.stats.title}
            </h2>
            <p className="mt-6 max-w-md text-base leading-relaxed text-[#5C6B64]">{t.stats.sub}</p>
            <div
              data-testid="stats-registry-seal"
              className="mt-8 inline-flex items-center gap-3 border border-[#E4DDD0] bg-white px-4 py-3"
            >
              <ShieldCheck className="h-4 w-4 text-[#B36C45]" />
              <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#5C6B64]">
                {t.stats.seal}
              </span>
            </div>
          </Reveal>

          <div className="grid grid-cols-1 sm:grid-cols-2">
            {t.stats.items.map((item, i) => (
              <Reveal
                key={item.label}
                delay={i * 0.1}
                className={`border-[#E4DDD0] p-8 sm:p-10 ${i % 2 === 0 ? "sm:border-r" : ""} ${
                  i < 2 ? "border-b" : "border-b sm:border-b-0"
                } ${i === 0 ? "border-t sm:border-t" : ""}`}
              >
                <div data-testid={`stat-item-${i}`}>
                  <Counter prefix={item.prefix} value={item.value} suffix={item.suffix} />
                  <p className="mt-4 font-mono text-[11px] uppercase tracking-[0.22em] text-[#5C6B64]">
                    {item.label}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
